// src/features/status/StatusModal.jsx
import React, { useEffect } from "react";


export default function StatusModal({
    open,
    onClose,

    // forventet ferdig-dato
    expectedReadyDate,           // string (ISO) eller ''/null
    onChangeExpectedReadyDate,   // (yyyy-mm-dd string|null) => void
    onSaveExpectedReadyDate,     // 👈 NY: (yyyy-mm-dd string) => void
    onRemoveExpectedReadyDate,   // () => void — fjerner ferdigdato helt

    // status
    currentStatus,
    statusOptions,
    selectedStatus,
    onSelectStatus,

    // melding / sms
    smsText,
    onChangeSmsText,

    // sms-maler
    smsTemplateLoading,
    smsTemplates,
    selectedTemplateId,
    onChangeTemplate,

    // kanaler
    sendOnStatusChange,
    onToggleSendSms,
    sendEmailOnStatusChange,
    onToggleSendEmail,

    // kontaktinfo
    customerPhoneLabel,
    customerEmailLabel,

    // actions
    onChangeStatusOnly,
    onSendStatusNotification,

    // busy
    updating,
    notifyBusy,
    emailBusy,
    smsBusy,

    msg,

    // kvitteringslenke
    showPortalLinkOption,
    linkBusy,
    includePortalLink,
    onTogglePortalLink,

    // vipps-betaling
    showVippsOption,
    vippsBusy,
    vippsRequested,
    onToggleVipps,
}) {

    // Sørg for at vi alltid har en valgt status når modal åpnes
    useEffect(() => {
        if (!open) return;
        if (!selectedStatus && currentStatus) {
            onSelectStatus(currentStatus);
        }
    }, [open, selectedStatus, currentStatus, onSelectStatus]);

    // Dropdown: nåværende status først, deretter resten uten duplikater
    const statusDropdownOptions = [
        currentStatus,
        ...(statusOptions || []),
    ].filter((v, i, arr) => v && arr.indexOf(v) === i);

    const hasEmail = !!String(customerEmailLabel || "").trim() &&
  !String(customerEmailLabel || "").toLowerCase().includes("mangler e-post");

// Hvis e-post mangler: slå av e-post-varsel automatisk når modalen er åpen
useEffect(() => {
  if (!open) return;
  if (!hasEmail && sendEmailOnStatusChange) {
    onToggleSendEmail(false);
  }
}, [open, hasEmail, sendEmailOnStatusChange, onToggleSendEmail]);


    if (!open) return null;

    const dateValue = expectedReadyDate
        ? String(expectedReadyDate).slice(0, 10)
        : "";

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const minDate = `${yyyy}-${mm}-${dd}`;

    function handleSaveDateClick() {
        if (!dateValue) return;

        // Formater dato pent til kunden
        const nice = new Date(dateValue).toLocaleDateString("no-NO");

        const text = `Hei, dato for når din service er estimert ferdig er endret til ${nice}.`;

        // Oppdater tekstfeltet i modal
        onChangeSmsText && onChangeSmsText(text);

        // Gi beskjed til parent om at datoen skal lagres på servicen
        onSaveExpectedReadyDate && onSaveExpectedReadyDate(dateValue);
    }

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-xl rounded shadow p-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-semibold">Endre status &amp; send SMS</h4>
                    <button
                        onClick={onClose}
                        className="text-gray-600 hover:text-black"
                    >
                        Lukk
                    </button>
                </div>

                {/* Forventet ferdig-dato */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Forventet ferdig-dato
                    </label>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                className="border rounded px-2 py-1 text-sm"
                                value={dateValue}
                                min={minDate}
                                onChange={(e) =>
                                    onChangeExpectedReadyDate &&
                                    onChangeExpectedReadyDate(e.target.value || null)
                                }
                            />
                            {dateValue && (
                                <span className="text-xs text-gray-500">
                                    Lagres som forventet ferdig.
                                </span>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={handleSaveDateClick}
                            disabled={!dateValue || updating}
                            className={`px-3 py-1 rounded text-sm text-white ${!dateValue || updating
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-emerald-600 hover:bg-emerald-700"
                                }`}
                        >
                            Lagre dato & legg i SMS
                        </button>
                    </div>
                </div>

                {/* Resten av komponenten er uendret */}
                <div className="mb-2">
                    Nåværende status:{" "}
                    <span className="font-medium">{currentStatus}</span>
                </div>

                {/* Status-dropdown */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Velg status
                    </label>

                    <select
                        className="w-full border rounded p-2 text-sm"
                        value={selectedStatus || currentStatus || ""}
                        onChange={(e) => onSelectStatus(e.target.value)}
                        disabled={updating}
                    >
                        {statusDropdownOptions.map((s) => (
                            <option key={s} value={s}>
                                {s === currentStatus ? `${s} (nåværende)` : s}
                            </option>
                        ))}
                    </select>

                    <div className="text-xs text-gray-500 mt-1">
                        Nåværende status vises alltid øverst.
                    </div>
                </div>


                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                        Varsler til kunde (valgfritt)
                    </label>

                    {/* SMS-maler */}
                    {smsTemplateLoading ? (
                        <div className="text-xs text-gray-500">
                            Henter SMS-maler …
                        </div>
                    ) : smsTemplates.length ? (
                        <div className="mb-1">
                            <label className="block text-xs text-gray-600 mb-1">
                                SMS-mal
                            </label>
                            <select
                                className="w-full border rounded p-2 text-sm"
                                value={selectedTemplateId}
                                onChange={onChangeTemplate}
                            >
                                <option value="">Velg mal …</option>
                                {smsTemplates.map((tpl) => (
                                    <option key={tpl._id} value={tpl._id}>
                                        {tpl.tittel} {tpl.type ? `– ${tpl.type}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : (
                        <div className="text-xs text-gray-500">
                            Ingen SMS-maler for denne kategorien. Du kan skrive meldingen
                            manuelt.
                        </div>
                    )}

                    {/* Meldingsfelt */}
                    <textarea
                        className="w-full border rounded p-2"
                        rows={3}
                        placeholder="Skriv en kort statusmelding …"
                        value={smsText}
                        onChange={(e) => onChangeSmsText(e.target.value)}
                    />

                    {/* Kanaler */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={sendOnStatusChange}
                                onChange={(e) => onToggleSendSms(e.target.checked)}
                            />
                            <span>Send SMS når status endres</span>
                            <span className="text-xs text-gray-500">
                                {customerPhoneLabel}
                            </span>
                        </label>

<label className={`flex items-center gap-2 ${!hasEmail ? "opacity-50 cursor-not-allowed" : ""}`}>
  <input
    type="checkbox"
    checked={hasEmail ? sendEmailOnStatusChange : false}
    onChange={(e) => onToggleSendEmail(e.target.checked)}
    disabled={!hasEmail}
  />
  <span>Send e-post når status endres</span>
  <span className="text-xs text-gray-500">
    {customerEmailLabel}
  </span>
</label>

                    </div>

                    {/* Kvitteringslenke */}
                    {showPortalLinkOption && (
                        <label className="flex items-start gap-2 mt-1 p-2 rounded border bg-emerald-50 border-emerald-200 cursor-pointer">
                            <div className="mt-0.5 w-4 h-4 shrink-0 flex items-center justify-center">
                                {linkBusy ? (
                                    <div className="w-3.5 h-3.5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <input
                                        type="checkbox"
                                        checked={!!includePortalLink}
                                        disabled={linkBusy}
                                        onChange={(e) => onTogglePortalLink(e.target.checked)}
                                    />
                                )}
                            </div>
                            <div>
                                <span className="text-sm font-medium text-emerald-900">Legg til kvitteringslenke i SMS</span>
                                <p className="text-xs text-emerald-700 mt-0.5">
                                    {linkBusy
                                        ? "Oppretter kundelenke…"
                                        : "Kunden ser utført arbeid og totalbeløp på sin serviceportal-side."}
                                </p>
                            </div>
                        </label>
                    )}

                    {/* Vipps-betaling */}
                    {showVippsOption && (
                        <label className="flex items-start gap-2 mt-1 p-2 rounded border bg-orange-50 border-orange-200 cursor-pointer">
                            <div className="mt-0.5 w-4 h-4 shrink-0 flex items-center justify-center">
                                {vippsBusy ? (
                                    <div className="w-3.5 h-3.5 border-2 border-[#FF5B24] border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <input
                                        type="checkbox"
                                        checked={!!vippsRequested}
                                        disabled={vippsBusy}
                                        onChange={(e) => onToggleVipps(e.target.checked)}
                                    />
                                )}
                            </div>
                            <div>
                                <span className="text-sm font-medium text-orange-900">Vippsbetaling</span>
                                <p className="text-xs text-orange-700 mt-0.5">
                                    Kunden får en "Betal med Vipps"-knapp på serviceportal-siden.
                                </p>
                            </div>
                        </label>
                    )}

                    {/* Handling-knapper */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        {!sendOnStatusChange && !sendEmailOnStatusChange ? (
                            <button
                                onClick={onChangeStatusOnly}
                                disabled={updating}
                                className={`px-3 py-1 rounded text-white ${updating
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                            >
                                {updating ? "Oppdaterer…" : "Endre status"}
                            </button>
                        ) : (
                            <button
                                onClick={onSendStatusNotification}
                                disabled={notifyBusy || emailBusy || smsBusy}
                                className={`px-3 py-1 rounded text-white ${notifyBusy || emailBusy || smsBusy
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-indigo-600 hover:bg-indigo-700"
                                    }`}
                            >
                                {notifyBusy || emailBusy ? "Sender …" : "Send"}
                            </button>
                        )}

                        <span className="text-xs text-gray-500">
                            {sendOnStatusChange ? "SMS " : ""}
                            {sendOnStatusChange && sendEmailOnStatusChange ? "+ " : ""}
                            {sendEmailOnStatusChange ? "e-post" : ""}
                        </span>
                    </div>

                    {dateValue && onRemoveExpectedReadyDate && (
                        <div>
                            <button
                                type="button"
                                onClick={onRemoveExpectedReadyDate}
                                disabled={updating}
                                className={`text-xs underline ${updating
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-red-600 hover:text-red-800"
                                    }`}
                            >
                                Fjern ferdigdato
                            </button>
                            <div className="text-xs text-gray-500 mt-0.5">
                                Servicen flyttes til «Manglende ferdigdato» i servicelisten.
                            </div>
                        </div>
                    )}

                    {msg && (
                        <div className="mt-2 text-sm text-gray-800">
                            {msg}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
